import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Save, RotateCcw, ImageIcon, Upload, Loader2 } from "lucide-react";

export default function ContentEditor() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState({}); // {key: value}
  const [dirty, setDirty] = useState({});   // {key: true}
  const [saving, setSaving] = useState({}); // {key: true}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/content");
      setSections(data.sections);
      const initial = {};
      data.sections.forEach(s => s.items.forEach(i => { initial[i.key] = i.value; }));
      setValues(initial);
      setDirty({});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onChange = (key, val) => {
    setValues(v => ({ ...v, [key]: val }));
    setDirty(d => ({ ...d, [key]: true }));
  };

  const save = async (item) => {
    setSaving(s => ({ ...s, [item.key]: true }));
    try {
      await api.put(`/admin/content/${item.key}`, { value: values[item.key], type: item.type });
      toast.success(`${item.label} mis à jour`);
      setDirty(d => { const n = { ...d }; delete n[item.key]; return n; });
      // refresh sections to update is_default flags
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(s => { const n = { ...s }; delete n[item.key]; return n; });
    }
  };

  const reset = async (item) => {
    if (!window.confirm(`Restaurer la valeur par défaut pour "${item.label}" ?`)) return;
    try {
      await api.delete(`/admin/content/${item.key}`);
      toast.success("Valeur restaurée");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const uploadImage = async (item, file) => {
    if (!file) return;
    setSaving(s => ({ ...s, [item.key]: true }));
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data.url;
      onChange(item.key, url);
      // Immediately save the new URL
      await api.put(`/admin/content/${item.key}`, { value: url, type: "image" });
      toast.success("Image mise à jour");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'upload");
    } finally {
      setSaving(s => { const n = { ...s }; delete n[item.key]; return n; });
    }
  };

  if (loading) return <div className="text-muted-foreground" data-testid="content-loading">Chargement…</div>;

  return (
    <div className="space-y-8" data-testid="content-editor">
      <div className="rounded-xl border border-primary/30 bg-primary/5 p-5">
        <h2 className="font-display text-2xl mb-2">Édition du contenu du site</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Modifiez ici les textes et images visibles publiquement sur le site.
          Les changements sont visibles immédiatement après sauvegarde — pensez à
          rafraîchir la page d'accueil pour les vérifier.
        </p>
      </div>

      {sections.map((section) => (
        <div key={section.section} className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`content-section-${section.section}`}>
          <div className="px-6 py-4 border-b border-border bg-input/40">
            <h3 className="font-display text-xl">{section.section}</h3>
          </div>
          <div className="p-6 space-y-6">
            {section.items.map((item) => (
              <ContentField
                key={item.key}
                item={item}
                value={values[item.key] ?? ""}
                isDirty={!!dirty[item.key]}
                isSaving={!!saving[item.key]}
                onChange={(v) => onChange(item.key, v)}
                onSave={() => save(item)}
                onReset={() => reset(item)}
                onUploadImage={(f) => uploadImage(item, f)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ContentField({ item, value, isDirty, isSaving, onChange, onSave, onReset, onUploadImage }) {
  return (
    <div className="grid lg:grid-cols-[1fr_2fr] gap-4 lg:gap-8 pb-6 border-b border-border last:border-0 last:pb-0" data-testid={`content-field-${item.key}`}>
      <div>
        <Label htmlFor={item.key} className="text-base">{item.label}</Label>
        <div className="text-[11px] text-muted-foreground mt-1 font-mono">{item.key}</div>
        {!item.is_default && item.updated_by && (
          <div className="text-[10px] text-primary/70 mt-2">
            Modifié par {item.updated_by.split("@")[0]}
            {item.updated_at && ` · ${new Date(item.updated_at).toLocaleDateString("fr-FR")}`}
          </div>
        )}
      </div>
      <div>
        {item.type === "image" ? (
          <ImageField item={item} value={value} isSaving={isSaving} onChange={onChange} onUploadImage={onUploadImage} onSave={onSave} onReset={onReset} isDirty={isDirty} />
        ) : item.type === "textarea" ? (
          <Textarea
            id={item.key}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={4}
            className="bg-input"
            data-testid={`content-input-${item.key}`}
          />
        ) : (
          <Input
            id={item.key}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="bg-input"
            data-testid={`content-input-${item.key}`}
          />
        )}
        {item.type !== "image" && (
          <div className="flex items-center gap-2 mt-3">
            <Button
              type="button"
              onClick={onSave}
              disabled={!isDirty || isSaving}
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
              data-testid={`content-save-${item.key}`}
            >
              {isSaving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              {isDirty ? "Enregistrer" : "Enregistré"}
            </Button>
            {!item.is_default && (
              <Button
                type="button"
                onClick={onReset}
                size="sm"
                variant="ghost"
                className="rounded-full text-xs"
                data-testid={`content-reset-${item.key}`}
              >
                <RotateCcw className="w-3 h-3 mr-1" /> Restaurer le défaut
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ImageField({ item, value, isSaving, onChange, onUploadImage, onSave, isDirty, onReset }) {
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-border bg-input/40 p-3 flex items-center gap-4">
        {value ? (
          <img src={value} alt={item.label} className="h-20 w-32 object-cover rounded border border-border" data-testid={`content-preview-${item.key}`} />
        ) : (
          <div className="h-20 w-32 flex items-center justify-center text-muted-foreground bg-input rounded border border-border">
            <ImageIcon className="w-6 h-6" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer text-sm" data-testid={`content-upload-${item.key}`}>
            {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Choisir une image
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={isSaving}
              onChange={(e) => onUploadImage(e.target.files?.[0])}
            />
          </label>
          {!item.is_default && (
            <Button
              type="button"
              onClick={onReset}
              size="sm"
              variant="ghost"
              className="rounded-full text-xs ml-2"
              data-testid={`content-reset-${item.key}`}
            >
              <RotateCcw className="w-3 h-3 mr-1" /> Défaut
            </Button>
          )}
        </div>
      </div>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Ou collez une URL d'image directement"
        className="bg-input text-xs font-mono"
        data-testid={`content-url-${item.key}`}
      />
      {isDirty && (
        <Button
          type="button"
          onClick={onSave}
          disabled={isSaving}
          size="sm"
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
          data-testid={`content-save-${item.key}`}
        >
          {isSaving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          Enregistrer l'URL
        </Button>
      )}
    </div>
  );
}
